// ── Nutrient-Condition Science Map ──────────────────────────────────────────
// Research-backed nutrient targets per mental health condition.
// Each nutrient includes: why it matters, key food sources, and daily target.
// Sources: peer-reviewed neuroscience, nutritional psychiatry research.

// ── KEY NUTRIENTS AND THEIR BRAIN FUNCTIONS ─────────────────────────────────
export const NUTRIENTS = {
  omega3:       { name: "Omega-3 (EPA/DHA)", unit: "mg", desc: "Anti-inflammatory, builds neuronal membranes, supports serotonin and dopamine transmission" },
  magnesium:    { name: "Magnesium", unit: "mg", desc: "Regulates NMDA receptors, calms nervous system, supports GABA production" },
  zinc:         { name: "Zinc", unit: "mg", desc: "Modulates glutamate/GABA balance, supports dopamine synthesis, essential for neuroplasticity" },
  iron:         { name: "Iron", unit: "mg", desc: "Oxygen transport to brain, cofactor for dopamine and serotonin synthesis" },
  b6:           { name: "Vitamin B6", unit: "mg", desc: "Converts tryptophan to serotonin, supports dopamine and GABA synthesis" },
  b12:          { name: "Vitamin B12", unit: "mcg", desc: "Myelin sheath maintenance, homocysteine regulation, prevents cognitive decline" },
  folate:       { name: "Folate (B9)", unit: "mcg", desc: "SAMe production for neurotransmitter synthesis, methylation support" },
  vitaminD:     { name: "Vitamin D", unit: "IU", desc: "Neuroprotective, regulates serotonin gene expression, reduces neuroinflammation" },
  tryptophan:   { name: "Tryptophan", unit: "mg", desc: "Direct precursor to serotonin and melatonin" },
  tyrosine:     { name: "Tyrosine", unit: "mg", desc: "Precursor to dopamine, norepinephrine, and epinephrine" },
  choline:      { name: "Choline", unit: "mg", desc: "Precursor to acetylcholine (memory, focus), supports cell membrane structure" },
  vitaminC:     { name: "Vitamin C", unit: "mg", desc: "Cofactor for dopamine and norepinephrine synthesis, potent antioxidant in brain" },
  selenium:     { name: "Selenium", unit: "mcg", desc: "Antioxidant defense via glutathione peroxidase, thyroid function, mood regulation" },
  probiotics:   { name: "Probiotics", unit: "servings", desc: "Gut-brain axis support, produces ~90% of body's serotonin, reduces inflammation" },
  fiber:        { name: "Fiber", unit: "g", desc: "Feeds beneficial gut bacteria, stabilizes blood sugar, supports sustained neurotransmitter production" },
  protein:      { name: "Protein", unit: "g", desc: "Provides amino acid building blocks for all neurotransmitters" },
  complexCarbs: { name: "Complex Carbohydrates", unit: "g", desc: "Steady glucose for brain fuel, facilitates tryptophan crossing blood-brain barrier" },
  antioxidants: { name: "Antioxidants", unit: "ORAC", desc: "Reduces oxidative stress and neuroinflammation, protects neurons" },
  nac:          { name: "NAC (N-Acetyl Cysteine)", unit: "mg", desc: "Glutathione precursor, modulates glutamate, reduces compulsive behavior" },
  lithium:      { name: "Lithium (trace)", unit: "mcg", desc: "Neuroprotective at trace levels, supports BDNF, mood stabilization" },
  creatine:     { name: "Creatine", unit: "g", desc: "Brain energy metabolism, supports ATP production in neurons" },
  inositol:     { name: "Inositol", unit: "mg", desc: "Second messenger in serotonin signaling, reduces anxiety and OCD symptoms" },
};

// ── CONDITION NUTRIENT PROFILES ─────────────────────────────────────────────
// priority: 1 = critical, 2 = important, 3 = supportive
// rationale: one-line research justification
export const CONDITION_NUTRIENTS = {
  adhd: {
    label: "ADHD",
    mechanism: "Dopamine and norepinephrine deficiency in prefrontal cortex",
    nutrients: [
      { id: "tyrosine",     priority: 1, rationale: "Direct precursor to dopamine, the primary deficient neurotransmitter in ADHD" },
      { id: "iron",         priority: 1, rationale: "Cofactor for tyrosine hydroxylase (rate-limiting enzyme in dopamine synthesis). 84% of ADHD children are iron deficient" },
      { id: "zinc",         priority: 1, rationale: "Modulates dopamine transporter. Low zinc correlates with inattention severity" },
      { id: "omega3",       priority: 1, rationale: "DHA comprises 40% of brain membrane fatty acids. ADHD brains show 38% lower omega-3 levels" },
      { id: "protein",      priority: 1, rationale: "Sustained amino acid delivery prevents dopamine crashes. Protein at breakfast reduces symptom severity" },
      { id: "magnesium",    priority: 2, rationale: "72% of ADHD children have low magnesium. Supports attention and reduces hyperactivity" },
      { id: "b6",           priority: 2, rationale: "Cofactor for DOPA decarboxylase in dopamine synthesis pathway" },
      { id: "vitaminD",     priority: 2, rationale: "ADHD prevalence 2-3x higher in vitamin D deficient populations" },
      { id: "choline",      priority: 2, rationale: "Supports acetylcholine for sustained attention and working memory" },
      { id: "complexCarbs", priority: 3, rationale: "Steady glucose prevents the energy crashes that worsen inattention" },
      { id: "fiber",        priority: 3, rationale: "Blood sugar stability reduces impulsivity spikes" },
    ],
    topFoods: ["salmon","eggs","chicken","turkey","beef","spinach","pumpkin seeds","walnuts","blueberries","sweet potato","lentils","dark chocolate"],
  },

  anxiety: {
    label: "Anxiety",
    mechanism: "Overactive amygdala, HPA axis dysregulation, low GABA, gut-brain axis disruption",
    nutrients: [
      { id: "magnesium",    priority: 1, rationale: "Blocks NMDA receptor overactivation in amygdala. Low magnesium directly increases anxiety behaviors" },
      { id: "omega3",       priority: 1, rationale: "EPA reduces neuroinflammation that drives anxiety. 20% reduction in anxiety symptoms in meta-analyses" },
      { id: "probiotics",   priority: 1, rationale: "Gut produces 90% of serotonin. Specific strains (L. rhamnosus) reduce anxiety via vagus nerve" },
      { id: "b6",           priority: 1, rationale: "Essential for GABA synthesis, the brain's primary calming neurotransmitter" },
      { id: "tryptophan",   priority: 2, rationale: "Serotonin precursor. Low serotonin is linked to generalized anxiety" },
      { id: "zinc",         priority: 2, rationale: "Modulates GABA-A receptors. Low zinc correlates with anxiety severity" },
      { id: "vitaminD",     priority: 2, rationale: "Vitamin D receptors in amygdala and hippocampus regulate fear response" },
      { id: "complexCarbs", priority: 2, rationale: "Facilitate tryptophan crossing BBB for serotonin production. Blood sugar crashes trigger anxiety" },
      { id: "antioxidants", priority: 3, rationale: "Oxidative stress in the brain amplifies anxiety signaling" },
      { id: "inositol",     priority: 3, rationale: "Second messenger in serotonin pathway. 12-18g/day shown to reduce panic attacks" },
      { id: "fiber",        priority: 3, rationale: "Prebiotic fiber feeds anxiety-reducing gut bacteria" },
    ],
    topFoods: ["spinach","salmon","yogurt","kefir","oats","turkey","chamomile","blueberries","avocado","sweet potato","almonds","dark chocolate"],
  },

  depression: {
    label: "Depression",
    mechanism: "Serotonin, dopamine, and BDNF deficiency; neuroinflammation; HPA axis dysregulation",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "EPA is as effective as antidepressants in mild-moderate depression (multiple RCTs). Reduces neuroinflammation" },
      { id: "folate",       priority: 1, rationale: "Required for SAMe production which drives serotonin/dopamine synthesis. Low folate predicts poor antidepressant response" },
      { id: "tryptophan",   priority: 1, rationale: "Direct serotonin precursor. Tryptophan depletion rapidly induces depressive symptoms" },
      { id: "b12",          priority: 1, rationale: "B12 deficiency mimics depression. Required for methylation and neurotransmitter production" },
      { id: "vitaminD",     priority: 1, rationale: "Low vitamin D strongly associated with depression. Supplementation improves symptoms in deficient individuals" },
      { id: "zinc",         priority: 2, rationale: "Lower zinc levels found in depressed individuals. Zinc augments antidepressant efficacy" },
      { id: "iron",         priority: 2, rationale: "Iron deficiency causes fatigue and anhedonia that mimics depression" },
      { id: "magnesium",    priority: 2, rationale: "Rapid antidepressant effects observed. Regulates HPA axis stress response" },
      { id: "probiotics",   priority: 2, rationale: "Gut dysbiosis found in 70% of depressed patients. Probiotics reduce depressive symptoms" },
      { id: "selenium",     priority: 3, rationale: "Low selenium correlates with depressed mood. Antioxidant protection for serotonin-producing neurons" },
      { id: "creatine",     priority: 3, rationale: "Brain creatine supports neuronal energy. Augments SSRI response in women" },
      { id: "antioxidants", priority: 3, rationale: "Depression associated with elevated oxidative stress markers" },
    ],
    topFoods: ["salmon","sardines","spinach","kale","turkey","eggs","lentils","walnuts","blueberries","dark chocolate","yogurt","sweet potato"],
  },

  bipolar: {
    label: "Bipolar Disorder",
    mechanism: "Mitochondrial dysfunction, oxidative stress, calcium signaling disruption, circadian rhythm instability",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "Reduces depressive episodes. EPA stabilizes neuronal membranes. Multiple positive RCTs" },
      { id: "magnesium",    priority: 1, rationale: "Natural mood stabilizer. Blocks excessive calcium influx. Supports circadian rhythm" },
      { id: "complexCarbs", priority: 1, rationale: "Blood sugar instability is a direct mood cycling trigger. Steady glucose prevents episodes" },
      { id: "folate",       priority: 2, rationale: "L-methylfolate augments mood stabilizer efficacy. Required for neurotransmitter balance" },
      { id: "vitaminD",     priority: 2, rationale: "Bipolar patients show significantly lower vitamin D. Seasonal pattern correlates with D levels" },
      { id: "nac",          priority: 2, rationale: "Glutathione precursor. Reduces depressive symptoms in bipolar (1g twice daily in RCTs)" },
      { id: "zinc",         priority: 2, rationale: "Supports mitochondrial function. Low zinc found during manic and depressive episodes" },
      { id: "choline",      priority: 3, rationale: "Choline supplementation reduces manic symptoms in some studies" },
      { id: "antioxidants", priority: 3, rationale: "Bipolar disorder shows elevated oxidative stress in both phases" },
      { id: "probiotics",   priority: 3, rationale: "Gut inflammation markers elevated in bipolar. Probiotics reduce rehospitalization rates" },
    ],
    topFoods: ["salmon","sardines","oats","sweet potato","spinach","walnuts","eggs","turkey","brown rice","lentils","yogurt","broccoli"],
  },

  ptsd: {
    label: "PTSD",
    mechanism: "Hyperactive amygdala, elevated cortisol, impaired hippocampal neurogenesis, vagal tone disruption",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "Reduces cortisol reactivity and amygdala hyperactivation. DHA supports hippocampal repair" },
      { id: "magnesium",    priority: 1, rationale: "Calms sympathetic nervous system. Blocks stress-induced NMDA overactivation" },
      { id: "tryptophan",   priority: 1, rationale: "PTSD depletes serotonin. Restoring tryptophan reduces hypervigilance" },
      { id: "vitaminD",     priority: 2, rationale: "Low vitamin D correlates with PTSD severity. Supports hippocampal neurogenesis" },
      { id: "probiotics",   priority: 2, rationale: "Vagus nerve connects gut to stress response. Probiotics improve vagal tone" },
      { id: "b6",           priority: 2, rationale: "Supports GABA production for calming overactive fear circuits" },
      { id: "zinc",         priority: 2, rationale: "Depleted by chronic stress. Supports neuroplasticity for trauma processing" },
      { id: "antioxidants", priority: 3, rationale: "Chronic stress generates oxidative damage. Antioxidants protect vulnerable neurons" },
      { id: "complexCarbs", priority: 3, rationale: "Steady blood sugar prevents cortisol spikes that trigger flashbacks" },
    ],
    topFoods: ["salmon","turkey","spinach","sweet potato","blueberries","yogurt","oats","walnuts","chamomile","avocado","eggs","kale"],
  },

  ocd: {
    label: "OCD",
    mechanism: "Serotonin dysfunction in cortico-striatal-thalamic circuits, glutamate dysregulation",
    nutrients: [
      { id: "tryptophan",   priority: 1, rationale: "OCD is fundamentally a serotonin disorder. Tryptophan depletion worsens OCD symptoms within hours" },
      { id: "inositol",     priority: 1, rationale: "Second messenger in serotonin signaling. 18g/day as effective as SSRIs in some OCD studies" },
      { id: "nac",          priority: 1, rationale: "Modulates glutamate, the excitatory neurotransmitter driving compulsive loops. Strong RCT evidence" },
      { id: "zinc",         priority: 2, rationale: "Augments SSRI response in OCD. Supports serotonin transporter function" },
      { id: "omega3",       priority: 2, rationale: "EPA augments SSRI efficacy in OCD. Anti-inflammatory" },
      { id: "complexCarbs", priority: 2, rationale: "Complex carbs facilitate tryptophan crossing BBB. Essential for sustained serotonin production" },
      { id: "b6",           priority: 2, rationale: "Cofactor in serotonin synthesis pathway" },
      { id: "magnesium",    priority: 3, rationale: "Supports GABA which counterbalances glutamate excitotoxicity in OCD" },
      { id: "probiotics",   priority: 3, rationale: "Gut bacteria produce serotonin precursors. Emerging evidence for OCD-gut connection" },
    ],
    topFoods: ["turkey","chicken","eggs","oats","brown rice","spinach","walnuts","salmon","yogurt","lentils","dark chocolate","bananas"],
  },

  bpd: {
    label: "Borderline PD",
    mechanism: "Amygdala hyperreactivity, prefrontal cortex underactivity, emotional dysregulation, rejection sensitivity",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "EPA reduces aggression and emotional reactivity in BPD (multiple RCTs). Supports prefrontal function" },
      { id: "magnesium",    priority: 1, rationale: "Calms amygdala reactivity. Blood sugar crashes amplify amygdala firing by up to 60%" },
      { id: "tryptophan",   priority: 1, rationale: "Low serotonin drives impulsivity and emotional instability in BPD" },
      { id: "complexCarbs", priority: 1, rationale: "Blood sugar stability IS emotional stability for BPD. Glucose crashes mimic emotional episodes" },
      { id: "zinc",         priority: 2, rationale: "Supports emotional regulation circuits. Low zinc increases irritability" },
      { id: "b6",           priority: 2, rationale: "Serotonin and GABA synthesis for emotional buffering" },
      { id: "iron",         priority: 2, rationale: "Iron deficiency causes irritability and emotional lability that worsens BPD" },
      { id: "probiotics",   priority: 3, rationale: "Gut inflammation amplifies emotional reactivity through the vagus nerve" },
      { id: "antioxidants", priority: 3, rationale: "Oxidative stress markers elevated in personality disorders" },
    ],
    topFoods: ["salmon","sardines","oats","sweet potato","spinach","turkey","eggs","avocado","blueberries","walnuts","lentils","brown rice"],
  },

  schizophrenia: {
    label: "Schizophrenia",
    mechanism: "Dopamine dysregulation, glutamate/GABA imbalance, neuroinflammation, oxidative stress",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "Reduces conversion to psychosis in high-risk individuals (landmark Vienna study). Anti-inflammatory" },
      { id: "antioxidants", priority: 1, rationale: "Schizophrenia shows severe oxidative stress. NAC and vitamin C reduce symptom severity" },
      { id: "folate",       priority: 1, rationale: "Methylfolate augments antipsychotic efficacy. Many patients have MTHFR variants" },
      { id: "b12",          priority: 1, rationale: "B12 deficiency can cause psychotic symptoms. Required for myelin maintenance" },
      { id: "nac",          priority: 2, rationale: "2g/day reduces negative symptoms. Modulates glutamate and provides antioxidant support" },
      { id: "vitaminD",     priority: 2, rationale: "Prenatal and current deficiency associated with schizophrenia risk and severity" },
      { id: "zinc",         priority: 2, rationale: "NMDA receptor modulator. Supports cognitive function in schizophrenia" },
      { id: "choline",      priority: 3, rationale: "Alpha-7 nicotinic receptor agonist. Supports auditory gating deficits" },
      { id: "probiotics",   priority: 3, rationale: "Gut inflammation increasingly linked to psychotic symptoms" },
    ],
    topFoods: ["salmon","sardines","spinach","kale","eggs","walnuts","blueberries","broccoli","sweet potato","lentils","yogurt","dark chocolate"],
  },

  autism: {
    label: "Autism Spectrum",
    mechanism: "Neuroinflammation, gut-brain axis disruption, oxidative stress, sensory processing differences",
    nutrients: [
      { id: "probiotics",   priority: 1, rationale: "GI issues in 70%+ of autistic individuals. Gut-brain axis modulation reduces irritability" },
      { id: "omega3",       priority: 1, rationale: "Reduces irritability and hyperactivity. DHA is critical for brain development and function" },
      { id: "b6",           priority: 1, rationale: "High-dose B6 with magnesium improves behavior and language in some studies" },
      { id: "magnesium",    priority: 1, rationale: "Used alongside B6. Calms sensory overload and reduces anxiety" },
      { id: "zinc",         priority: 2, rationale: "Many autistic individuals are zinc deficient. Supports sensory processing and immunity" },
      { id: "folate",       priority: 2, rationale: "Folinic acid improves language in autism (FOCAS trial). Cerebral folate deficiency common" },
      { id: "vitaminD",     priority: 2, rationale: "Lower vitamin D levels found. Supplementation may improve core symptoms" },
      { id: "fiber",        priority: 3, rationale: "Supports gut microbiome diversity, often reduced in autism" },
      { id: "antioxidants", priority: 3, rationale: "Elevated oxidative stress markers. Antioxidants provide neuroprotection" },
    ],
    topFoods: ["yogurt","kefir","salmon","eggs","chicken","bananas","sweet potato","blueberries","oats","avocado","broccoli","rice"],
  },

  did: {
    label: "DID",
    mechanism: "Trauma-related dissociation, HPA axis disruption, energy regulation instability",
    nutrients: [
      { id: "complexCarbs", priority: 1, rationale: "Blood sugar crashes can trigger dissociative episodes. Steady glucose is grounding" },
      { id: "protein",      priority: 1, rationale: "Consistent amino acid delivery supports stable neurotransmitter levels across states" },
      { id: "omega3",       priority: 2, rationale: "Anti-inflammatory, supports trauma recovery and hippocampal function" },
      { id: "magnesium",    priority: 2, rationale: "Calms nervous system, supports grounding" },
      { id: "b12",          priority: 2, rationale: "Energy and cognitive stability" },
      { id: "iron",         priority: 2, rationale: "Prevents fatigue that worsens dissociation" },
      { id: "tryptophan",   priority: 3, rationale: "Serotonin stability across alter states" },
      { id: "probiotics",   priority: 3, rationale: "Gut-brain axis support for emotional regulation" },
    ],
    topFoods: ["chicken","eggs","oats","sweet potato","rice","turkey","salmon","bananas","yogurt","lentils","avocado","spinach"],
  },

  eating: {
    label: "Eating Disorders",
    mechanism: "Malnutrition-driven neurotransmitter depletion, fear of food, body image distortion",
    nutrients: [
      { id: "zinc",         priority: 1, rationale: "Zinc deficiency impairs taste and appetite. Supplementation restores appetite in anorexia" },
      { id: "b12",          priority: 1, rationale: "Often depleted. Critical for energy and preventing cognitive decline from malnutrition" },
      { id: "iron",         priority: 1, rationale: "Anemia common. Iron restores energy and prevents exercise intolerance" },
      { id: "complexCarbs", priority: 1, rationale: "Gentle, non-threatening energy source. Stabilizes blood sugar for emotional safety" },
      { id: "protein",      priority: 2, rationale: "Rebuilds depleted amino acid stores. Supports neurotransmitter recovery" },
      { id: "omega3",       priority: 2, rationale: "Anti-inflammatory, supports brain recovery from starvation damage" },
      { id: "vitaminD",     priority: 2, rationale: "Bone health (osteoporosis risk) and mood support" },
      { id: "magnesium",    priority: 3, rationale: "Electrolyte balance, prevents cardiac complications of refeeding" },
      { id: "probiotics",   priority: 3, rationale: "Gut microbiome severely disrupted by restriction. Rebuilding supports recovery" },
    ],
    topFoods: ["oats","rice","chicken","eggs","bananas","yogurt","sweet potato","salmon","avocado","toast","lentils","berries"],
  },

  npd: {
    label: "Narcissistic PD",
    mechanism: "Serotonin dysregulation, prefrontal cortex underactivity, empathy circuit disruption",
    nutrients: [
      { id: "tryptophan",   priority: 1, rationale: "Low serotonin linked to aggression, impulsivity, and reduced empathy" },
      { id: "omega3",       priority: 1, rationale: "EPA supports prefrontal cortex function and reduces impulsive aggression" },
      { id: "magnesium",    priority: 2, rationale: "Reduces irritability and supports emotional regulation" },
      { id: "b6",           priority: 2, rationale: "Serotonin synthesis cofactor" },
      { id: "probiotics",   priority: 2, rationale: "Gut-brain axis influences emotional processing and social cognition" },
      { id: "zinc",         priority: 3, rationale: "Supports frontal lobe function and impulse control" },
      { id: "antioxidants", priority: 3, rationale: "Reduces neuroinflammation that impairs empathy circuits" },
    ],
    topFoods: ["turkey","salmon","pumpkin seeds","spinach","yogurt","walnuts","eggs","oats","dark chocolate","lentils","sweet potato","chicken"],
  },

  hpd: {
    label: "Histrionic PD",
    mechanism: "Emotional regulation instability, serotonin and dopamine dysregulation, reward sensitivity",
    nutrients: [
      { id: "tryptophan",   priority: 1, rationale: "Serotonin stability reduces emotional volatility and attention-seeking" },
      { id: "omega3",       priority: 1, rationale: "Supports emotional regulation and prefrontal cortex function" },
      { id: "magnesium",    priority: 2, rationale: "Mood stabilization, reduces emotional reactivity" },
      { id: "probiotics",   priority: 2, rationale: "Gut-brain axis support for mood regulation" },
      { id: "complexCarbs", priority: 2, rationale: "Blood sugar stability prevents emotional swings" },
      { id: "b6",           priority: 3, rationale: "Serotonin and GABA synthesis" },
      { id: "zinc",         priority: 3, rationale: "Emotional regulation support" },
    ],
    topFoods: ["turkey","salmon","sweet potato","yogurt","oats","spinach","eggs","walnuts","bananas","chicken","blueberries","avocado"],
  },

  aspd: {
    label: "Antisocial PD",
    mechanism: "Prefrontal cortex hypoactivation, reduced serotonin, impaired empathy, impulsivity",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "EPA reduces aggression by 34% in RCTs. Supports prefrontal cortex function and impulse control" },
      { id: "tryptophan",   priority: 1, rationale: "Low serotonin directly linked to impulsive aggression and antisocial behavior" },
      { id: "zinc",         priority: 2, rationale: "Supports frontal lobe executive function and impulse regulation" },
      { id: "magnesium",    priority: 2, rationale: "Reduces aggression and irritability. Supports GABA" },
      { id: "b6",           priority: 2, rationale: "Serotonin synthesis for impulse control" },
      { id: "iron",         priority: 3, rationale: "Iron deficiency increases irritability and aggression" },
      { id: "probiotics",   priority: 3, rationale: "Emerging research on gut-aggression link" },
    ],
    topFoods: ["salmon","sardines","turkey","pumpkin seeds","spinach","eggs","walnuts","lentils","dark chocolate","avocado","chicken","yogurt"],
  },

  phobia: {
    label: "Phobias",
    mechanism: "Amygdala hyperactivation, conditioned fear response, low GABA",
    nutrients: [
      { id: "magnesium",    priority: 1, rationale: "Blocks NMDA receptors in amygdala, reducing fear response intensity" },
      { id: "omega3",       priority: 1, rationale: "Reduces amygdala reactivity and supports fear extinction learning" },
      { id: "b6",           priority: 1, rationale: "GABA synthesis, the primary inhibitory neurotransmitter that dampens fear" },
      { id: "tryptophan",   priority: 2, rationale: "Serotonin modulates fear circuits" },
      { id: "probiotics",   priority: 2, rationale: "L. rhamnosus reduces anxiety and fear behavior via vagus nerve" },
      { id: "zinc",         priority: 2, rationale: "GABA-A receptor modulator" },
      { id: "vitaminD",     priority: 3, rationale: "Vitamin D receptors in amygdala regulate fear processing" },
      { id: "antioxidants", priority: 3, rationale: "Reduces oxidative stress that amplifies fear signaling" },
    ],
    topFoods: ["salmon","spinach","almonds","yogurt","turkey","blueberries","avocado","dark chocolate","oats","eggs","sweet potato","walnuts"],
  },

  bfrb: {
    label: "Body-Focused Repetitive Behaviors",
    mechanism: "Glutamate dysregulation, habitual loop in cortico-striatal circuits, oxidative stress",
    nutrients: [
      { id: "nac",          priority: 1, rationale: "NAC modulates glutamate in nucleus accumbens. 1200-2400mg/day reduces hair pulling/skin picking in RCTs" },
      { id: "omega3",       priority: 2, rationale: "Anti-inflammatory, supports impulse control circuits" },
      { id: "b6",           priority: 2, rationale: "Supports serotonin and GABA, which modulate compulsive behavior" },
      { id: "magnesium",    priority: 2, rationale: "GABA support to counterbalance glutamate driving repetitive behavior" },
      { id: "zinc",         priority: 2, rationale: "Supports serotonin transporter function" },
      { id: "antioxidants", priority: 3, rationale: "Elevated oxidative stress in BFRBs. Antioxidants support brain recovery" },
      { id: "probiotics",   priority: 3, rationale: "Gut-brain axis influences compulsive behavior patterns" },
    ],
    topFoods: ["broccoli","eggs","chicken","spinach","walnuts","blueberries","salmon","garlic","dark chocolate","yogurt","lentils","sweet potato"],
  },

  ppd: {
    label: "Paranoid PD",
    mechanism: "Amygdala hypervigilance, dopamine dysregulation, impaired trust circuits",
    nutrients: [
      { id: "magnesium",    priority: 1, rationale: "Blocks NMDA overactivation that drives hypervigilance. Calms threat detection" },
      { id: "omega3",       priority: 1, rationale: "Reduces neuroinflammation driving paranoid ideation. Supports social cognition" },
      { id: "tryptophan",   priority: 2, rationale: "Serotonin moderates trust and social perception" },
      { id: "probiotics",   priority: 2, rationale: "Gut-brain axis influences social cognition and anxiety" },
      { id: "b6",           priority: 2, rationale: "GABA synthesis to counterbalance hypervigilant fear circuits" },
      { id: "antioxidants", priority: 3, rationale: "Neuroinflammation reduction" },
      { id: "vitaminD",     priority: 3, rationale: "Supports social cognition and amygdala regulation" },
    ],
    topFoods: ["salmon","spinach","pumpkin seeds","yogurt","turkey","walnuts","blueberries","sweet potato","oats","chicken","kefir","dark chocolate"],
  },

  spd: {
    label: "Schizoid PD",
    mechanism: "Dopamine underutilization in reward circuits, reduced motivation, emotional flatness",
    nutrients: [
      { id: "tyrosine",     priority: 1, rationale: "Precursor to dopamine, the neurotransmitter of motivation and reward that SPD underutilizes" },
      { id: "omega3",       priority: 1, rationale: "DHA supports cognitive function and social brain circuits" },
      { id: "iron",         priority: 2, rationale: "Cofactor for dopamine synthesis. Deficiency worsens anhedonia" },
      { id: "b12",          priority: 2, rationale: "Supports cognitive function and energy" },
      { id: "zinc",         priority: 2, rationale: "Supports dopamine signaling and cognitive function" },
      { id: "creatine",     priority: 3, rationale: "Brain energy metabolism to counter cognitive sluggishness" },
      { id: "choline",      priority: 3, rationale: "Acetylcholine supports motivation and engagement" },
    ],
    topFoods: ["eggs","chicken","beef","salmon","spinach","lentils","dark chocolate","avocado","walnuts","sweet potato","turkey","blueberries"],
  },

  neuro_core: {
    label: "Neuro Core (General Optimization)",
    mechanism: "Overall brain performance, neuroprotection, cognitive longevity",
    nutrients: [
      { id: "omega3",       priority: 1, rationale: "Foundation of brain membrane health. DHA is 40% of brain fatty acids" },
      { id: "antioxidants", priority: 1, rationale: "Protects neurons from oxidative damage and aging" },
      { id: "choline",      priority: 1, rationale: "Memory, focus, and neuroprotection via acetylcholine" },
      { id: "magnesium",    priority: 2, rationale: "Supports 300+ enzymatic reactions including neurotransmitter synthesis" },
      { id: "vitaminD",     priority: 2, rationale: "Neuroprotective, anti-inflammatory, supports cognitive function" },
      { id: "b12",          priority: 2, rationale: "Myelin maintenance and cognitive preservation" },
      { id: "folate",       priority: 2, rationale: "Methylation support for neurotransmitter balance" },
      { id: "zinc",         priority: 2, rationale: "Neuroplasticity and synaptic signaling" },
      { id: "protein",      priority: 3, rationale: "Amino acid building blocks for all neurotransmitters" },
      { id: "probiotics",   priority: 3, rationale: "Gut-brain axis optimization" },
    ],
    topFoods: ["salmon","eggs","blueberries","spinach","walnuts","dark chocolate","avocado","broccoli","sweet potato","yogurt","sardines","turmeric"],
  },
};

// ── INGREDIENT NUTRIENT DATABASE ────────────────────────────────────────────
// Key brain-health nutrients per ingredient (per typical serving)
// Values are relative scores (1-10) for how rich the food is in each nutrient
export const FOOD_NUTRIENTS = {
  // ── Fish & Seafood ──
  salmon:       { omega3: 10, protein: 9, vitaminD: 9, b12: 8, selenium: 8, tyrosine: 7, tryptophan: 6 },
  sardines:     { omega3: 10, b12: 10, vitaminD: 8, selenium: 8, protein: 8, iron: 5, calcium: 7 },
  tuna:         { omega3: 7, protein: 9, selenium: 10, b12: 8, vitaminD: 5, tyrosine: 7 },
  mackerel:     { omega3: 10, b12: 10, vitaminD: 8, selenium: 7, protein: 8 },
  shrimp:       { protein: 8, selenium: 9, b12: 6, zinc: 5, choline: 5 },
  cod:          { protein: 8, b12: 6, selenium: 7, omega3: 3, vitaminD: 4 },

  // ── Poultry ──
  chicken:      { protein: 10, tyrosine: 8, tryptophan: 7, b6: 7, choline: 5, selenium: 7, zinc: 4 },
  turkey:       { protein: 9, tryptophan: 10, tyrosine: 8, b6: 6, selenium: 7, zinc: 5 },

  // ── Red Meat ──
  beef:         { protein: 10, iron: 9, zinc: 9, b12: 10, tyrosine: 8, creatine: 8, choline: 5 },
  lamb:         { protein: 9, iron: 7, zinc: 8, b12: 9, tyrosine: 7 },
  pork:         { protein: 9, b12: 5, zinc: 5, iron: 4, tyrosine: 6, selenium: 7 },

  // ── Eggs & Dairy ──
  egg:          { choline: 10, protein: 8, b12: 6, vitaminD: 4, selenium: 6, tryptophan: 5, tyrosine: 5, iron: 3 },
  yogurt:       { probiotics: 9, protein: 6, b12: 5, calcium: 8, tryptophan: 4, zinc: 3 },
  kefir:        { probiotics: 10, protein: 5, b12: 5, calcium: 7 },
  cheese:       { protein: 6, b12: 5, calcium: 8, zinc: 4, tryptophan: 4 },

  // ── Legumes ──
  lentils:      { folate: 10, iron: 8, fiber: 9, protein: 7, magnesium: 6, zinc: 5, complexCarbs: 7 },
  chickpeas:    { folate: 7, fiber: 8, protein: 6, magnesium: 5, zinc: 5, iron: 5, complexCarbs: 7 },
  blackBeans:   { fiber: 9, folate: 8, iron: 6, magnesium: 7, protein: 7, complexCarbs: 7, antioxidants: 6 },

  // ── Nuts & Seeds ──
  walnuts:      { omega3: 8, antioxidants: 8, magnesium: 5, zinc: 4, b6: 3, fiber: 4 },
  almonds:      { magnesium: 8, vitaminE: 8, fiber: 5, protein: 5, iron: 3, zinc: 3 },
  pumpkinSeeds: { zinc: 10, magnesium: 9, iron: 6, omega3: 3, protein: 5, tryptophan: 6 },
  flaxseeds:    { omega3: 9, fiber: 8, magnesium: 5, b6: 3 },
  cashews:      { magnesium: 7, zinc: 6, iron: 5, protein: 4 },

  // ── Vegetables ──
  spinach:      { folate: 10, magnesium: 8, iron: 7, vitaminC: 5, antioxidants: 7, fiber: 5, b6: 4 },
  kale:         { vitaminC: 10, antioxidants: 9, folate: 5, magnesium: 4, fiber: 5, iron: 3 },
  broccoli:     { vitaminC: 9, folate: 6, fiber: 6, antioxidants: 7, nac: 5 },
  sweetPotato:  { complexCarbs: 9, vitaminC: 5, fiber: 6, b6: 7, antioxidants: 7, magnesium: 4 },
  avocado:      { magnesium: 6, fiber: 7, b6: 5, folate: 6, potassium: 8, omega3: 2 },
  brusselsSprouts: { vitaminC: 8, folate: 5, fiber: 6, antioxidants: 6, nac: 4 },
  asparagus:    { folate: 8, fiber: 4, vitaminC: 4, antioxidants: 5 },
  bellPepper:   { vitaminC: 10, antioxidants: 7, b6: 5, folate: 3 },
  tomato:       { vitaminC: 5, antioxidants: 8, folate: 3, fiber: 3 },
  mushroom:     { vitaminD: 7, selenium: 7, b6: 3, antioxidants: 5 },
  garlic:       { antioxidants: 7, nac: 4, selenium: 3 },

  // ── Fruits ──
  blueberries:  { antioxidants: 10, vitaminC: 5, fiber: 5, manganese: 7 },
  strawberries: { vitaminC: 9, antioxidants: 7, fiber: 4, folate: 4 },
  bananas:      { b6: 8, magnesium: 5, tryptophan: 4, complexCarbs: 6, fiber: 4, potassium: 8 },
  oranges:      { vitaminC: 10, folate: 5, fiber: 4, antioxidants: 5 },
  mango:        { vitaminC: 7, antioxidants: 6, b6: 4, fiber: 4 },
  kiwi:         { vitaminC: 10, fiber: 5, antioxidants: 5, folate: 3 },
  pomegranate:  { antioxidants: 10, vitaminC: 5, fiber: 5, folate: 4 },
  cherries:     { antioxidants: 8, vitaminC: 4, fiber: 3, melatonin: 7 },
  watermelon:   { vitaminC: 4, antioxidants: 5, b6: 3 },

  // ── Grains ──
  oats:         { fiber: 9, complexCarbs: 9, magnesium: 6, iron: 5, b6: 3, zinc: 4, protein: 4 },
  brownRice:    { complexCarbs: 8, fiber: 5, magnesium: 5, selenium: 5, b6: 4 },
  quinoa:       { protein: 7, magnesium: 7, iron: 5, fiber: 6, complexCarbs: 7, folate: 4, zinc: 4 },

  // ── Other ──
  darkChocolate:{ antioxidants: 9, magnesium: 7, iron: 5, fiber: 4, zinc: 3 },
  turmeric:     { antioxidants: 10, antiInflammatory: 10 },
  oliveOil:     { omega3: 3, antioxidants: 6 },
  kimchi:       { probiotics: 9, vitaminC: 3, fiber: 3 },
  sauerkraut:   { probiotics: 8, vitaminC: 4, fiber: 3 },
};

// ── SCORING FUNCTION ────────────────────────────────────────────────────────
// Score a meal's ingredients against a condition's nutrient needs
export const scoreMealForCondition = (ingredientKeys, conditionId) => {
  const profile = CONDITION_NUTRIENTS[conditionId];
  if (!profile) return { score: 0, maxScore: 0, percent: 0, hits: [] };

  let score = 0;
  let maxScore = 0;
  const hits = [];

  for (const nutrient of profile.nutrients) {
    const weight = nutrient.priority === 1 ? 3 : nutrient.priority === 2 ? 2 : 1;
    maxScore += weight * 10; // max possible per nutrient

    let bestValue = 0;
    let bestFood = null;
    for (const foodKey of ingredientKeys) {
      const foodProfile = FOOD_NUTRIENTS[foodKey];
      if (foodProfile && foodProfile[nutrient.id]) {
        if (foodProfile[nutrient.id] > bestValue) {
          bestValue = foodProfile[nutrient.id];
          bestFood = foodKey;
        }
      }
    }

    if (bestValue > 0) {
      score += weight * bestValue;
      hits.push({ nutrient: nutrient.id, value: bestValue, food: bestFood, priority: nutrient.priority });
    }
  }

  return {
    score,
    maxScore,
    percent: maxScore > 0 ? Math.round((score / maxScore) * 100) : 0,
    hits,
  };
};

// ── DETECT INGREDIENTS FROM MEAL NAME ───────────────────────────────────────
export const detectIngredients = (mealName) => {
  const n = mealName.toLowerCase();
  const found = [];

  const checks = [
    ["salmon", /salmon|lox/],
    ["sardines", /sardine/],
    ["tuna", /tuna/],
    ["mackerel", /mackerel/],
    ["shrimp", /shrimp|prawn/],
    ["cod", /\bcod\b|tilapia|halibut|trout|sea bass|snapper|mahi/],
    ["chicken", /chicken/],
    ["turkey", /turkey/],
    ["beef", /beef|steak|sirloin|ribeye|brisket|burger|meatloaf/],
    ["lamb", /lamb/],
    ["pork", /pork|bacon|ham\b|prosciutto/],
    ["egg", /\beggs?\b|omelette|omelet|frittata|scramble/],
    ["yogurt", /yogurt|parfait/],
    ["kefir", /kefir/],
    ["cheese", /cheese|feta|mozzarella|parmesan|cheddar/],
    ["lentils", /lentil/],
    ["chickpeas", /chickpea|hummus|falafel/],
    ["blackBeans", /black bean/],
    ["walnuts", /walnut/],
    ["almonds", /almond/],
    ["pumpkinSeeds", /pumpkin seed/],
    ["flaxseeds", /flax/],
    ["cashews", /cashew/],
    ["spinach", /spinach/],
    ["kale", /kale/],
    ["broccoli", /broccoli/],
    ["sweetPotato", /sweet potato/],
    ["avocado", /avocado/],
    ["brusselsSprouts", /brussels|brussel/],
    ["asparagus", /asparagus/],
    ["bellPepper", /bell pepper|pepper/],
    ["tomato", /tomato|pico|salsa|marinara/],
    ["mushroom", /mushroom/],
    ["garlic", /garlic/],
    ["blueberries", /blueberr/],
    ["strawberries", /strawberr/],
    ["bananas", /banana/],
    ["oranges", /orange(?!.*juice)/],
    ["mango", /mango/],
    ["kiwi", /kiwi/],
    ["pomegranate", /pomegranate/],
    ["cherries", /cherr(?!.*tomato)/],
    ["watermelon", /watermelon/],
    ["oats", /oat|granola|porridge/],
    ["brownRice", /rice/],
    ["quinoa", /quinoa/],
    ["darkChocolate", /dark chocolate|chocolate/],
    ["turmeric", /turmeric/],
    ["kimchi", /kimchi/],
    ["oliveOil", /olive oil/],
  ];

  for (const [key, pattern] of checks) {
    if (pattern.test(n)) found.push(key);
  }

  return found;
};
